import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Stream from 'flarum/common/utils/Stream';
import avatarCache from '../../common/utils/AvatarCache';
import ImageCropModal from './ImageCropModal';

// Phase 1 i18n — translator.trans() 返回 vnode array，flatten 为 string。
// WHY: see memory/flarum-1.8-pitfalls.md 坑 B
function t(key) {
  const result = app.translator.trans(key);
  return Array.isArray(result) ? result.join('') : result;
}

// 有占位符的翻译（"已选 N 件"），保留 vnode array 让 mithril 当 children 渲染。
// WHY: t() flatten 会丢 vnode，导致 rich placeholder 失效。tRich() 保留 array 结构。
function tRich(key, params) {
  return app.translator.trans(key, params || {});
}
export default class AvatarModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.loading = true;
    this.avatars = [];
    this.selectedId = null;
    this.errorMessage = '';

    // 从父组件获取当前选中的形象
    this.selectedAvatar = this.attrs.selectedAvatar || null;

    // 上传相关状态
    this.showUploadForm = false;
    this.uploading = false;
    this.previewUrl = Stream('');
    this.selectedFile = null;
    this.uploadError = '';

    // 表单数据
    this.height = Stream('');
    this.weight = Stream('');
    this.gender = Stream(0);

    // 编辑模式状态
    this.isEditMode = false;
    this.editingAvatar = null;
    // v0.1.2: 删除确认状态（替代原生 confirm()）
    this.deleteConfirmId = null;

    // 拖拽状态计数器（解决子元素触发dragleave的问题）
    this.dragCounter = 0;

    // v0.4.3: Modal 大小状态（上传/编辑表单用 small，否则用 large 默认）
    this.isSmall = false;

    // 加载形象列表
    this.loadAvatars();
  }
  className() {
    // v0.4.5: 必须把 ModalManager 的 'in' class 写进 className()。
    // 原因: ModalManager.animateShow() 用 requestAnimationFrame 把 'in' class
    // 加到 modal 元素上 (见 vendor/flarum/core/js/src/common/components/ModalManager.tsx:159)。
    // 但当我们在 click handler 里同步 m.redraw() 时, mithril 会用 className() 的
    // 返回值覆写 classList —— 把 'in' 冲掉, modal 透明度从 1 渐变回 0 (Flarum 的
    // .Modal { transition: opacity 0.2s } 让这个过程明显, 看起来和"modal 消失"一样)。
    // 解法: className() 直接包含 'in' (modal open 时)。同步 redraw 之后 classList
    // 仍然包含 'in', 透明度保持 1。
    // 注意: Modal 关闭走 animateHide → rAF 移除 'in' + 'out' class → transitionend
    // → ModalManagerState.close() → mithril 卸载元素, 这条路径不受影响。
    const size = this.isSmall ? 'Modal--small' : 'Modal--large';
    return "".concat(size, " AvatarModal Modal in");
  }
  onReady() {
    // 阻止 ESC 键关闭 Modal
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }
  handleKeyDown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
    }
  }
  onremove() {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
  }
  renderContent() {
    return this.content();
  }
  title() {
    if (this.isEditMode) {
      return t('ziven-dress-up.forum.avatar_modal.title_edit');
    }
    return t('ziven-dress-up.forum.avatar_modal.title_select');
  }
  async loadAvatars() {
    this.loading = true;
    m.redraw();
    try {
      const response = await app.request({
        method: 'GET',
        url: '/api/ziven/seedream/avatars'
      });
      if (response.success) {
        this.avatars = response.data || [];

        // 设置当前选中的ID
        const selected = this.avatars.find(a => a.is_selected);
        if (selected) {
          this.selectedId = selected.id;
        }
      }
    } catch (error) {
      console.error('Failed to load avatars:', error);
      this.errorMessage = t('ziven-dress-up.forum.avatar_modal.load_error');
    }
    this.loading = false;
    m.redraw();
  }
  showUploadSection(e) {
    // Bug fix: 改用 onclick 触发 (替换原 onmousedown)。
    // 原因: mousedown + e.preventDefault() 会让浏览器把后续 click 事件派发到 mousedown 时
    //       的原始 target; 但 m.redraw() 同步替换了 DOM, 原始 target 已被卸载;
    //       在某些浏览器/时序下, click 会被 re-target 到 ModalManager-invisibleBackdrop (sibling),
    //       触发其 onclick → handlePossibleBackdropClick → animateHide → modal 消失。
    //       改用 onclick 后: 一次 click 事件完整触发, m.redraw() 在 click handler 内同步执行,
    //       浏览器把 click 派发到 click 触发时的 target (即新 DOM), 不会再误命中 backdrop。
    if (e) {
      e.stopPropagation();
    }
    this.isEditMode = false;
    this.editingAvatar = null;
    this.showUploadForm = true;
    this.previewUrl('');
    this.selectedFile = null;
    this.uploadError = '';
    this.height('');
    this.weight('');
    this.gender('0');
    // v0.4.3: 用 className() 动态切 Modal 大小（替代 jQuery 改 DOM，会被 mithril 重渲染覆盖）
    this.isSmall = true;
    m.redraw();
  }
  showEditSection(avatar, e) {
    // v0.4.4: 事件隔离 (方案 2 fix) — 强化 e.stopImmediatePropagation 防止 click 冒泡到 ModalManager invisibleBackdrop
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation && e.stopImmediatePropagation();
    }
    this.isEditMode = true;
    this.editingAvatar = avatar;
    this.showUploadForm = true;
    this.previewUrl(avatar.avatar_url || '');
    this.selectedFile = null;
    this.uploadError = '';
    this.height(avatar.height ? String(avatar.height) : '');
    this.weight(avatar.weight ? String(avatar.weight) : '');
    this.gender(avatar.gender !== undefined ? String(avatar.gender) : '0');
    // v0.4.3: 用 className() 动态切 Modal 大小（替代 jQuery 改 DOM，会被 mithril 重渲染覆盖）
    this.isSmall = true;
    m.redraw();
  }
  hideUploadSection(e) {
    // v0.4.4: 事件隔离 (方案 2 fix) — 防止 click 事件冒泡到 ModalManager invisibleBackdrop 触发 close
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.showUploadForm = false;
    this.isEditMode = false;
    this.editingAvatar = null;
    this.previewUrl('');
    this.selectedFile = null;
    this.uploadError = '';
    // v0.4.3: 用 className() 动态切 Modal 大小（替代 jQuery 改 DOM，会被 mithril 重渲染覆盖）
    this.isSmall = false;
    m.redraw();
  }
  async handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;

    // 验证文件类型
    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      this.uploadError = t('ziven-dress-up.forum.avatar_modal.invalid_type');
      m.redraw();
      return;
    }

    // 验证文件大小 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      this.uploadError = tRich('ziven-dress-up.forum.avatar_modal.file_too_large', {
        size: 10
      });
      m.redraw();
      return;
    }

    // 安全检查：验证文件头
    const reader = new FileReader();
    const self = this;
    reader.onload = function (event) {
      const dataUrl = event.target.result;
      if (!dataUrl || !dataUrl.startsWith('data:')) {
        self.uploadError = t('ziven-dress-up.forum.avatar_modal.read_error');
        m.redraw();
        return;
      }

      // 验证 magic bytes
      try {
        const base64Data = dataUrl.split(',')[1];
        const binaryString = atob(base64Data.substring(0, 24));
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const isJpeg = bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF;
        const isPng = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47;
        if (!isJpeg && !isPng) {
          self.uploadError = t('ziven-dress-up.forum.avatar_modal.invalid_content');
          m.redraw();
          return;
        }
      } catch (err) {
        self.uploadError = t('ziven-dress-up.forum.avatar_modal.invalid_content_short');
        m.redraw();
        return;
      }
      self.selectedFile = file;
      self.previewUrl(dataUrl);
      self.uploadError = '';
      m.redraw();

      // v0.4.7 #365 — 上传后弹剪裁 modal (开关 ON 时)
      // Flarum modal: 第 3 个参数 stackModal=true 让新 modal 叠在现有 modal 上方
      // (默认 false 会替换当前 modal — 关闭 crop 后 AvatarModal 也跟着没)
      // 检查开关: 默认 ON (localStorage 'seedream.cropEnabled' === 'false' 才算 OFF)
      if (typeof localStorage !== 'undefined' && localStorage.getItem('seedream.cropEnabled') !== 'false') {
        app.modal.show(ImageCropModal, {
          imageDataUrl: dataUrl,
          onConfirm: (croppedBlob, croppedDataUrl) => {
            // 剪裁完成 — 用剪裁后的 blob/dataUrl 替换 selectedFile/previewUrl
            self.selectedFile = croppedBlob;
            self.previewUrl(croppedDataUrl);
            self.uploadError = '';
            m.redraw();
          },
          onCancel: () => {
            // 辉哥 22:23 亲测 bug — 取消剪裁应该回到空的上传框 (清空 selectedFile/previewUrl)
            // v0.4.7 #365 原 onCancel 是空实现导致图片仍显示 — 修正
            self.selectedFile = null;
            self.previewUrl('');
            self.uploadError = '';
            m.redraw();
          }
        }, true); // stackModal=true 叠在 AvatarModal 上
      }
    };
    reader.onerror = function () {
      self.uploadError = t('ziven-dress-up.forum.avatar_modal.read_error');
      m.redraw();
    };
    reader.readAsDataURL(file);
  }
  removeImage() {
    this.selectedFile = null;
    this.previewUrl('');
    m.redraw();
  }
  async submitUpload() {
    if (!this.isEditMode && !this.selectedFile) {
      this.uploadError = t('ziven-dress-up.forum.avatar_modal.need_upload');
      m.redraw();
      return;
    }
    this.uploading = true;
    this.uploadError = '';
    m.redraw();
    try {
      const formData = new FormData();
      if (this.isEditMode && this.editingAvatar) {
        // 编辑模式：更新已有形象
        formData.append('id', this.editingAvatar.id);
        const height = parseFloat(this.height());
        const weight = parseFloat(this.weight());
        const gender = parseInt(this.gender()) || 0;
        if (height > 0 && height < 300) {
          formData.append('height', height);
        }
        if (weight > 0 && weight < 500) {
          formData.append('weight', weight);
        }
        if (gender >= 0 && gender <= 2) {
          formData.append('gender', gender);
        }

        // 如果选择了新图片
        if (this.selectedFile) {
          formData.append('avatar', this.selectedFile);
        }
        const response = await app.request({
          method: 'PUT',
          url: '/api/ziven/seedream/avatars/' + this.editingAvatar.id,
          body: formData
        });
        if (response.success) {
          // v0.1.2 P3: 成功清空错误
          this.uploadError = '';
          // 更新列表中的数据
          const index = this.avatars.findIndex(a => a.id === this.editingAvatar.id);
          if (index !== -1) {
            this.avatars[index] = response.data;
          }
          this.showUploadForm = false;
          this.isEditMode = false;
          this.editingAvatar = null;
          this.previewUrl('');
          this.selectedFile = null;
          // v0.4.6: 编辑成功 reset isSmall (跟初始 Modal--large 一致, 避免 modal 停留在 Modal--small 视觉不一致)
          this.isSmall = false;
          m.redraw();
        } else {
          this.uploadError = response.error || t('ziven-dress-up.forum.avatar_modal.update_error');
        }
      } else {
        // 新增模式
        formData.append('avatar', this.selectedFile);
        const height = parseFloat(this.height());
        const weight = parseFloat(this.weight());
        const gender = parseInt(this.gender()) || 0;
        if (height > 0 && height < 300) {
          formData.append('height', height);
        }
        if (weight > 0 && weight < 500) {
          formData.append('weight', weight);
        }
        if (gender >= 0 && gender <= 2) {
          formData.append('gender', gender);
        }
        const response = await app.request({
          method: 'POST',
          url: '/api/ziven/seedream/avatars',
          body: formData
        });
        if (response.success) {
          // v0.1.2 P3: 成功清空错误
          this.uploadError = '';
          this.avatars.unshift(response.data);
          this.showUploadForm = false;
          this.previewUrl('');
          this.selectedFile = null;
          // v0.4.6: 上传成功 reset isSmall (跟初始 Modal--large 一致, 避免 modal 停留在 Modal--small 视觉不一致)
          this.isSmall = false;
          m.redraw();
        } else {
          this.uploadError = response.error || t('ziven-dress-up.forum.avatar_modal.upload_error');
        }
      }
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      this.uploadError = this.isEditMode ? t('ziven-dress-up.forum.avatar_modal.update_error') : t('ziven-dress-up.forum.avatar_modal.upload_error');
    }
    this.uploading = false;
    m.redraw();
  }
  async selectAvatar(avatar) {
    if (this.selectedId === avatar.id) {
      this.confirmSelection(avatar);
      return;
    }
    try {
      const formData = new FormData();
      formData.append('id', avatar.id);
      const response = await app.request({
        method: 'PUT',
        url: '/api/ziven/seedream/avatars/select',
        body: formData
      });
      if (response.success) {
        this.avatars.forEach(a => a.is_selected = false);
        avatar.is_selected = true;
        this.selectedId = avatar.id;
        if (avatar.avatar_url) {
          await avatarCache.downloadAndCacheImage(avatar.avatar_url, avatar.id);
        }
        this.confirmSelection(avatar);
      }
    } catch (error) {
      console.error('Failed to select avatar:', error);
      this.errorMessage = t('ziven-dress-up.forum.avatar_modal.select_error');
      m.redraw();
    }
  }
  confirmSelection(avatar) {
    if (this.attrs.onSelect) {
      this.attrs.onSelect(avatar);
    }
    this.hide();
  }
  requestDelete(avatar) {
    this.deleteConfirmId = avatar.id;
    m.redraw();
  }
  cancelDelete() {
    this.deleteConfirmId = null;
    m.redraw();
  }
  async confirmDelete(avatar) {
    this.deleteConfirmId = null;
    try {
      const response = await app.request({
        method: 'DELETE',
        url: '/api/ziven/seedream/avatars/' + avatar.id
      });
      if (response.success) {
        if (this.selectedId === avatar.id) {
          await avatarCache.deleteSelectedAvatar();
        }
        this.avatars = this.avatars.filter(a => a.id !== avatar.id);
        if (this.selectedId === avatar.id) {
          this.selectedId = null;
          if (this.attrs.onSelect) {
            this.attrs.onSelect(null);
          }
        }
        m.redraw();
      }
    } catch (error) {
      console.error('Failed to delete avatar:', error);
      this.errorMessage = t('ziven-dress-up.forum.avatar_modal.delete_error');
      m.redraw();
    }
  }
  content() {
    if (this.showUploadForm) {
      return this.renderUploadForm();
    }
    return m("div", {
      class: "Modal-body"
    }, this.errorMessage && m("div", {
      class: "error-message"
    }, this.errorMessage), this.loading ? m("div", {
      class: "loading-area"
    }, m(LoadingIndicator, null), m("span", null, ' ' + t('ziven-dress-up.forum.common.loading'))) : this.avatars.length === 0 ?
    // v0.4.7.4 (Mavis 紧急修, 2026-07-31): empty 状态也用 3 列 grid
    //   之前 v0.4.7.3 把 avatar-upload-placeholder 直接放 .empty-area 里
    //   被 padding:60px 20px 拉成全宽, 辉哥 07:47 反馈"添加新形象按钮占据整个 modal"
    //   修法: empty 状态复用 .AvatarModal-grid + 把 placeholder 放第 1 个 cell
    //   跟有数据时的 grid 视觉完全一致 (mockup Page2)
    m("div", {
      class: "AvatarModal-grid"
    }, m("div", {
      class: "avatar-upload-placeholder",
      onclick: e => this.showUploadSection(e)
    }, m("i", {
      class: "fas fa-plus"
    }), m("span", null, ' ' + t('ziven-dress-up.forum.avatar_modal.add_new')), m("p", null, ' ' + t('ziven-dress-up.forum.avatar_modal.supported_formats')))) : m("div", {
      class: "AvatarModal-grid"
    }, this.avatars.map(avatar => m("div", {
      class: "avatar-card ".concat(this.selectedId === avatar.id ? 'selected' : ''),
      key: avatar.id,
      onmousedown: e => {
        e.preventDefault();
        e.stopPropagation();
        this.selectAvatar(avatar);
      }
    }, m("img", {
      src: avatar.avatar_url,
      alt: "Avatar"
    }), this.selectedId === avatar.id && m("div", {
      class: "selected-overlay"
    }, m("i", {
      class: "fas fa-check"
    })), m("div", {
      class: "avatar-actions"
    }, m("button", {
      type: "button",
      class: "edit-btn",
      onmousedown: e => this.showEditSection(avatar, e),
      title: t('ziven-dress-up.forum.avatar_modal.action_edit')
    }, m("i", {
      class: "fas fa-edit"
    })), this.deleteConfirmId === avatar.id ? m('[', null, m("button", {
      type: "button",
      class: "delete-confirm-btn",
      onmousedown: e => {
        e.stopPropagation();
        e.preventDefault();
        this.confirmDelete(avatar);
      },
      title: t('ziven-dress-up.forum.avatar_modal.action_confirm_delete')
    }, m("i", {
      class: "fas fa-check"
    })), m("button", {
      type: "button",
      class: "delete-cancel-btn",
      onmousedown: e => {
        e.stopPropagation();
        e.preventDefault();
        this.cancelDelete();
      },
      title: t('ziven-dress-up.forum.avatar_modal.action_cancel')
    }, m("i", {
      class: "fas fa-times"
    }))) : m("button", {
      type: "button",
      class: "delete-btn",
      onmousedown: e => {
        e.stopPropagation();
        e.preventDefault();
        this.requestDelete(avatar);
      },
      title: t('ziven-dress-up.forum.avatar_modal.action_delete')
    }, m("i", {
      class: "fas fa-trash"
    }))))), m("div", {
      class: "avatar-upload-placeholder",
      onclick: e => this.showUploadSection(e)
    }, m("i", {
      class: "fas fa-plus"
    }), m("span", null, ' ' + t('ziven-dress-up.forum.avatar_modal.add_new')), m("p", null, ' ' + t('ziven-dress-up.forum.avatar_modal.supported_formats')))), m("div", {
      class: "AvatarModal-footer"
    }, m(Button, {
      class: "btn-glass",
      onclick: () => app.modal.close()
    }, m("i", {
      class: "fas fa-arrow-left"
    }), ' ' + t('ziven-dress-up.forum.avatar_modal.back')), m(Button, {
      class: "btn-amber",
      disabled: !this.selectedAvatar,
      onclick: () => this.confirmSelection(this.selectedAvatar)
    }, m("i", {
      class: "fas fa-check"
    }), ' ' + t('ziven-dress-up.forum.avatar_modal.confirm'))));
  }
  renderUploadForm() {
    return m("div", {
      class: "Modal-body"
    }, this.uploadError && m("div", {
      class: "error-message"
    }, this.uploadError), m("div", {
      class: "upload-avatar-section"
    }, m("div", {
      class: "upload-avatar-box",
      onmousedown: e => {
        e.preventDefault();
        e.stopPropagation();
        this.triggerFileInput();
      },
      ondragenter: e => this.handleDragEnter(e),
      ondragover: e => this.handleDragOver(e),
      ondragleave: e => this.handleDragLeave(e),
      ondrop: e => this.handleDrop(e)
    }, m("input", {
      type: "file",
      id: "avatar-upload-input",
      accept: "image/jpeg,image/jpg,image/png,image/heic,image/heif,image/webp",
      capture: "user",
      onchange: e => this.handleFileSelect(e),
      style: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0,
        cursor: 'pointer',
        zIndex: 10
        // iOS 关键: opacity:0 + 覆盖层, 不能 display:none
      }
    }), this.previewUrl() ? m("div", {
      class: "avatar-preview-container"
    }, m("img", {
      src: this.previewUrl(),
      alt: "Preview"
    }), m("div", {
      class: "preview-overlay",
      onmousedown: e => {
        e.preventDefault();
        e.stopPropagation();
        this.triggerFileInput();
      }
    }, m("i", {
      class: "fas fa-camera"
    }), m("span", null, ' ' + t('ziven-dress-up.forum.avatar_modal.click_change'))), m("button", {
      type: "button",
      class: "remove-btn",
      onmousedown: e => {
        e.stopPropagation();
        e.preventDefault();
        this.removeImage();
      }
    }, m("i", {
      class: "fas fa-times"
    }))) : m("div", {
      class: "upload-placeholder"
    }, m("i", {
      class: "fas fa-user-circle"
    }), m("span", null, ' ' + (this.isEditMode ? t('ziven-dress-up.forum.avatar_modal.upload_hint_edit') : t('ziven-dress-up.forum.avatar_modal.upload_hint_new'))), m("span", {
      class: "upload-hint"
    }, ' ' + tRich('ziven-dress-up.forum.avatar_modal.upload_format_hint', {
      size: 10
    }))), m("input", {
      type: "file",
      id: "avatar-upload-input",
      accept: "image/jpeg,image/png",
      onchange: e => this.handleFileSelect(e),
      style: {
        display: 'none'
      }
    })), this.isEditMode && m("p", {
      class: "edit-hint"
    }, ' ' + t('ziven-dress-up.forum.avatar_modal.edit_keep_hint'))), m("div", {
      class: "avatar-form-section"
    }, m("h4", null, ' ' + t('ziven-dress-up.forum.avatar_modal.personal_info')), m("div", {
      class: "form-row"
    }, m("label", null, m("i", {
      class: "fas fa-ruler-vertical"
    }), m("span", null, ' ' + t('ziven-dress-up.forum.avatar_modal.height_label'))), m("input", {
      type: "number",
      class: "form-input",
      placeholder: t('ziven-dress-up.forum.avatar_modal.height_placeholder'),
      min: "50",
      max: "250",
      value: this.height(),
      oninput: e => this.height(e.target.value)
    })), m("div", {
      class: "form-row"
    }, m("label", null, m("i", {
      class: "fas fa-weight"
    }), m("span", null, ' ' + t('ziven-dress-up.forum.avatar_modal.weight_label'))), m("input", {
      type: "number",
      class: "form-input",
      placeholder: t('ziven-dress-up.forum.avatar_modal.weight_placeholder'),
      min: "20",
      max: "300",
      value: this.weight(),
      oninput: e => this.weight(e.target.value)
    })), m("div", {
      class: "form-row"
    }, m("label", null, m("i", {
      class: "fas fa-venus-mars"
    }), m("span", null, ' ' + t('ziven-dress-up.forum.avatar_modal.gender_label'))), m("select", {
      class: "form-input",
      value: this.gender(),
      onchange: e => this.gender(e.target.value)
    }, m("option", {
      value: "0"
    }, ' ' + t('ziven-dress-up.forum.avatar_modal.gender_unknown')), m("option", {
      value: "1"
    }, ' ' + t('ziven-dress-up.forum.avatar_modal.gender_male')), m("option", {
      value: "2"
    }, ' ' + t('ziven-dress-up.forum.avatar_modal.gender_female'))))), m("div", {
      class: "modal-actions"
    }, m(Button, {
      class: "btn-cancel",
      onclick: e => this.hideUploadSection(e)
    }, ' ' + t('ziven-dress-up.forum.avatar_modal.cancel')), m(Button, {
      class: "btn-submit",
      disabled: this.uploading || !this.isEditMode && !this.selectedFile,
      onclick: () => this.submitUpload()
    }, this.uploading ? m('[', null, m(LoadingIndicator, null), m("span", null, ' ' + (this.isEditMode ? t('ziven-dress-up.forum.avatar_modal.saving') : t('ziven-dress-up.forum.avatar_modal.uploading')))) : m('[', null, m("i", {
      class: this.isEditMode ? 'fas fa-save' : 'fas fa-upload'
    }), m("span", null, ' ' + (this.isEditMode ? t('ziven-dress-up.forum.avatar_modal.save') : t('ziven-dress-up.forum.avatar_modal.upload')))))));
  }
  triggerFileInput() {
    const input = document.getElementById('avatar-upload-input');
    if (input) {
      input.click();
    }
  }

  // ========== 拖拽上传支持 ==========
  handleDragEnter(e) {
    // 只在桌面端启用（检测是否为触控设备）
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
    e.preventDefault();
    e.stopPropagation();

    // 增加计数器
    this.dragCounter++;

    // 添加拖拽样式
    const target = e.currentTarget;
    if (target && this.dragCounter === 1) {
      target.classList.add('drag-over');
    }
  }
  handleDragOver(e) {
    // 只在桌面端启用
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
    e.preventDefault();
    e.stopPropagation();
  }
  handleDragLeave(e) {
    // 只在桌面端启用
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
    e.preventDefault();
    e.stopPropagation();

    // 减少计数器
    this.dragCounter--;

    // 只有当计数器为0时才移除样式
    const target = e.currentTarget;
    if (target && this.dragCounter <= 0) {
      target.classList.remove('drag-over');
      this.dragCounter = 0;
    }
  }
  handleDrop(e) {
    // 只在桌面端启用
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
    e.preventDefault();
    e.stopPropagation();

    // 重置计数器
    this.dragCounter = 0;
    const target = e.currentTarget;
    if (target) {
      target.classList.remove('drag-over');
    }
    const files = e.dataTransfer.files;
    if (files.length === 0) return;
    const mockEvent = {
      target: {
        files: [files[0]],
        value: files[0].name
      }
    };
    this.handleFileSelect(mockEvent);
  }
}
_defineProperty(AvatarModal, "isDismissibleViaBackdropClick", false);
_defineProperty(AvatarModal, "isDismissibleViaCloseButton", true);
flarum.reg.add('ziiven-dress-up', 'forum/components/AvatarModal', AvatarModal);